import React, { useCallback, useEffect, useState } from "react";
import { Button, Modal, Spin, Switch, message } from "antd";

import * as api from "../controller";
import OfficeDetailDrawer from "../OfficeDetailDrawer";
import "../leadSettings.css";
import "./billing.css";

const ok = (r) => r && r.status >= 200 && r.status < 300;

/**
 * CRM Settings → Plans & trials: the two global switches (usage.TrialConfig).
 *
 *  - Free trials for everyone: off keeps trials per invite (each CRM invite
 *    decides) — the normal setting.
 *  - Require a plan: on, an office with no paid plan, no trial and no
 *    complimentary access gets "Choose a plan" on every product page. Before it
 *    goes on, this lists exactly who that would lock out, so they can be given a
 *    trial or complimentary access first (from each office's Users & Billing tab).
 */
export default function PlansSettingsPanel() {
  const [data, setData] = useState(null);
  const [office, setOffice] = useState(null); // {officeId, practiceId} open in the drawer
  const [failed, setFailed] = useState(false);
  const [saving, setSaving] = useState(null);

  const load = useCallback(async () => {
    const r = await api.getPlanSettings();
    setFailed(!ok(r));
    if (ok(r)) setData(r.json);
  }, []);
  useEffect(() => { load(); }, [load]);

  const save = async (field, value) => {
    setSaving(field);
    const r = await api.updatePlanSettings({ [field]: value });
    setSaving(null);
    if (ok(r)) {
      setData(r.json);
      message.success("Saved. Offices pick this up within a minute.");
    } else {
      message.error((r.json && r.json.detail) || "Couldn't save.");
    }
  };

  const toggleRequire = (next) => {
    if (!next) { save("require_plan", false); return; }
    const blocked = (data && data.would_block) || [];
    Modal.confirm({
      title: "Require a plan?",
      width: 520,
      okText: "Require a plan",
      content: blocked.length ? (
        <>
          <p>
            {blocked.length === 1 ? "This office has" : `These ${blocked.length} offices have`} no plan, no trial
            and no complimentary access, so {blocked.length === 1 ? "it" : "they"} will be asked to choose a plan
            on every product page:
          </p>
          <ul style={{ paddingLeft: 18, margin: 0 }}>
            {blocked.map((o) => <li key={o.office_id}>{o.office_name}</li>)}
          </ul>
        </>
      ) : (
        <p>Every office already has a plan, a trial or complimentary access — nobody is locked out.</p>
      ),
      onOk: () => save("require_plan", true),
    });
  };

  if (failed && !data) {
    return (
      <p className="crm-sub">
        Couldn&rsquo;t load the plan settings.{" "}
        <Button size="small" type="link" style={{ padding: 0 }} onClick={load}>Try again</Button>
      </p>
    );
  }
  if (!data) return <Spin />;

  const blocked = data.would_block || [];
  return (
    <div className="leadset-panel">
      <div className="leadset-row">
        <div className="leadset-row-label">
          Require a plan
          <div className="leadset-hint">
            {data.require_plan
              ? "On — an office with no paid plan, trial or complimentary access sees “Choose a plan” on every product page. Settings, Billing and Users stay open."
              : "Off — an office that never had a trial keeps using Smilepass without a plan. An office whose trial ran out is asked to choose a plan either way."}
          </div>
        </div>
        <div className="leadset-row-control">
          <Switch checked={!!data.require_plan} loading={saving === "require_plan"} onChange={toggleRequire} />
        </div>
      </div>
      {!data.require_plan ? (
        blocked.length ? (
          <div className="ub-callout is-warn" style={{ margin: "0 0 8px" }}>
            Switching it on would lock out {blocked.length} office{blocked.length === 1 ? "" : "s"}:{" "}
            {blocked.map((o, i) => (
              <React.Fragment key={o.office_id}>
                {i ? ", " : ""}
                <button
                  type="button"
                  className="ub-link"
                  onClick={() => setOffice({ officeId: o.office_id, practiceId: o.practice_id })}
                >
                  {o.office_name}
                </button>
              </React.Fragment>
            ))}
            . Open each to give it a trial or complimentary access (Users &amp; Billing tab) first.
          </div>
        ) : (
          <div className="leadset-hint" style={{ margin: "0 0 8px" }}>
            Every office has a plan, a trial or complimentary access — switching it on locks nobody out.
          </div>
        )
      ) : null}
      <div className="leadset-row">
        <div className="leadset-row-label">
          Free trials for everyone
          <div className="leadset-hint">
            {data.free_trials_enabled
              ? `On — every new practice may start a free trial of ${data.trial_request_quota} requests per office.`
              : "Off — trials are granted per invite (the invite's free-trial switch), or per office from its Users & Billing tab."}
          </div>
        </div>
        <div className="leadset-row-control">
          <Switch
            checked={!!data.free_trials_enabled}
            loading={saving === "free_trials_enabled"}
            onChange={(v) => save("free_trials_enabled", v)}
          />
        </div>
      </div>
      <OfficeDetailDrawer
        officeId={office ? office.officeId : null}
        practiceId={office ? office.practiceId : null}
        initialTab="users"
        onClose={() => { setOffice(null); load(); }}
      />
    </div>
  );
}
